interface StepColors {
	icons: string[]
	text: {
		primary: string
		light: string
		lighter: string
	}
	border: {
		right: {
			primary: string
		}
		left: {
			primary: string
		}
		primary: string
		light: string
		lighter: string
	}
	background: {
		primary: string
		light: string
		lighter: string
	}
	shadow?: string
}

const stepColors = {
	healthy: {
		icons: ["check"],
		text: {
			primary: "text-blue-500",
			light: "text-blue-400",
			lighter: "text-blue-300",
		},
		border: {
			right: {
				primary: "border-r-blue-500",
			},
			left: {
				primary: "border-l-blue-500",
			},
			primary: "border-blue-500",
			light: "border-blue-400",
			lighter: "border-blue-300",
		},
		background: {
			primary: "bg-blue-500",
			light: "bg-blue-400",
			lighter: "bg-blue-300",
		},
		shadow: "shadow-blue-500",
	},
	final: {
		icons: ["shield"],
		text: {
			primary: "text-green-500",
			light: "text-green-400",
			lighter: "text-green-300",
		},
		border: {
			right: {
				primary: "border-r-green-500",
			},
			left: {
				primary: "border-l-green-500",
			},
			primary: "border-green-500",
			light: "border-green-400",
			lighter: "border-green-300",
		},
		background: {
			primary: "bg-green-500",
			light: "bg-green-400",
			lighter: "bg-green-300",
		},
		shadow: "shadow-green-500",
	},
	warning: {
		icons: ["triangle-exclamation"],
		text: {
			primary: "text-yellow-500",
			light: "text-yellow-400",
			lighter: "text-yellow-300",
		},
		border: {
			right: {
				primary: "border-r-yellow-500",
			},
			left: {
				primary: "border-l-yellow-500",
			},
			primary: "border-yellow-500",
			light: "border-yellow-400",
			lighter: "border-yellow-300",
		},
		background: {
			primary: "bg-yellow-500",
			light: "bg-yellow-400",
			lighter: "bg-yellow-300",
		},
		shadow: "shadow-yellow-500",
	},
	error: {
		icons: ["ban"],
		text: {
			primary: "text-red-500",
			light: "text-red-400",
			lighter: "text-red-300",
		},
		border: {
			right: {
				primary: "border-r-red-500",
			},
			left: {
				primary: "border-l-red-500",
			},
			primary: "border-red-500",
			light: "border-red-400",
			lighter: "border-red-300",
		},
		background: {
			primary: "bg-red-500",
			light: "bg-red-400",
			lighter: "bg-red-300",
		},
		shadow: "shadow-red-500",
	},
}

const getStepColors = (status: string): StepColors => {
	if (status === "healthy") return stepColors.healthy
	else if (status === "final") return stepColors.final
	else if (status === "warning") return stepColors.warning
	else if (status === "error") return stepColors.error
	else return stepColors.error // Default to error if status is unknown
}

const buildTooltipMessage = (messages: string[]) =>
	messages && messages.length ? '<ul class="list-disc pl-5">' + messages.map((m) => `<li>${m}</li>`).join("") + "</ul>" : null

const buildComponentClasses = (isActive: boolean, stepColors: StepColors) => {
	const iconClasses = isActive
		? ["bg-white", stepColors.text.primary, stepColors.border.primary]
		: [stepColors.background.primary, stepColors.text.lighter, stepColors.border.primary]
	const divClasses = isActive
		? [stepColors.background.primary, stepColors.text.lighter, stepColors.border.light]
		: [stepColors.text.primary, stepColors.border.light]
	return {
		iconClasses,
		divClasses,
	}
}

// Pastel colors for template group backgrounds (shared between step list rows and graph groups)
const templateGroupPalette = [
	"#fef3c7", // amber-100
	"#dbeafe", // blue-100
	"#dcfce7", // green-100
	"#fce7f3", // pink-100
	"#e0e7ff", // indigo-100
	"#fae8ff", // fuchsia-100
	"#ccfbf1", // teal-100
	"#fee2e2", // red-100
	"#f3e8ff", // purple-100
	"#cffafe", // cyan-100
]

// Build a color map from templateGroupId to palette color, preserving consistent order
const buildTemplateGroupColorMap = (items: { templateGroupId?: string | null }[]): Map<string, string> => {
	const map = new Map<string, string>()
	let colorIdx = 0
	for (const item of items) {
		const groupId = item.templateGroupId
		if (groupId && !map.has(groupId)) {
			map.set(groupId, templateGroupPalette[colorIdx % templateGroupPalette.length])
			colorIdx++
		}
	}
	return map
}

export type { StepColors }
export { getStepColors, buildTooltipMessage, buildComponentClasses, templateGroupPalette, buildTemplateGroupColorMap }
